import DataTable from '../../components/DataTable'
import { getOrderReadyColumns } from '../../components/TableColumns'
import { useEffect, useState, useContext, useMemo } from 'react'
import { DataStore, SortDirection } from '@aws-amplify/datastore'
import { DgtPdProductionOrder } from '../../models'
import { syncQueriesReady, removeSync } from '../../sync/DataStoreSync'
import { format } from 'date-fns'
import { 
	OrderStatusDisplayPdLeaderline,
	OrderType,
	OrderStatusCode,
	Division,
	DivisionValue,
	OrderNotes,
	OrderNoteDisplays
} from '../../constants/Enum'
import SyncExpressionContext from '../../contexts/SyncExpressionContext'
import UserContext from '../../contexts/UserContext';
import { getISOStartOfDate, getISOEndOfDate } from '../../utilities/Format'

import Select from 'react-select';
import DatePicker from 'react-datepicker'

import { useForm } from 'react-hook-form'

import { getOrder } from '../../apis'

const ProductionOrders = () => {
	const { setSyncOrderStatus, syncPeriod, setSyncPeriod } = useContext(SyncExpressionContext)
	const { user } = useContext(UserContext)

	const [productionOrders, setProductionOrders] = useState([])
	const [orderType, setOrderType] = useState(OrderType.ALL)
	const [orderStatus, setOrderStatus] = useState(OrderStatusCode.READY)
	const [division, setDivision] = useState(user?.division || DivisionValue.HHD)
	const [orderNote, setOrderNote] = useState(OrderNotes.ALL)

	const [startDate, setStartDate] = useState(syncPeriod[0]);
	const [endDate, setEndDate] = useState(syncPeriod[1]);

	const [loading, setLoading] = useState(false)

	const { register, handleSubmit, watch } = useForm();
  
	const onSubmitSearch = async (data) => {
		if (!data.pdOrderNo) return

		setLoading(true)
		const remoteOrder = await getOrder(data.pdOrderNo)
		setLoading(false)
		if (remoteOrder) {
			const mapDateFormatOrders = [remoteOrder].map(ord => {
				return {
					...ord,
					dueDate: format(new Date(ord.dueDate), 'dd/MM/yyyy')
				}
			})
			setProductionOrders(mapDateFormatOrders)
		}
	}

	const watchOrderId = watch('pdOrderNo')

	useEffect(() => {
		if (watchOrderId) {
			fetchData(ord => ord.id("contains", watchOrderId))
		} else {
			fetchData(getCurentFilter(startDate, endDate, orderType, orderStatus, division))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ watchOrderId ])

	const getCurentFilter = (start, end, type, status, div) => {
		return ord => {
			let baseFilter = ord.updatedAt("between", [getISOStartOfDate(start), getISOEndOfDate(end)])
			if (orderType !== OrderType.ALL) {
				baseFilter = baseFilter.materialType('eq', type)
			}
			if (orderStatus !== 'ALL') {
				baseFilter = baseFilter.statusFromApp('eq', status)
			}
			if (division !== 'ALL') {
				baseFilter = baseFilter.division('eq', div)
			}
			return baseFilter
		}
	}

	const fetchData = async (filterFunc, sortFunction) => {
		try {
			if (!sortFunction) sortFunction = s => s.id(SortDirection.ASCENDING)
			const orders = await DataStore.query(DgtPdProductionOrder, filterFunc, {
				sort: sortFunction
			});
			const mapDateFormatOrders = orders.map(ord => {
				return {
					...ord,
					dueDate: format(new Date(ord.dueDate), 'dd/MM/yyyy')
				}
			})
			setProductionOrders(mapDateFormatOrders)
		} catch (error) {
			console.log("Error retrieving ProductionOrders", error);
		}
	}

	useEffect(() => {
		const listener = syncQueriesReady(fetchData, getCurentFilter(startDate, endDate, orderType, orderStatus, division))
		return () => removeSync(listener)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [startDate, endDate, orderType, orderStatus, division])

	const onTypeChanged = (e) => {
		setOrderType(e.value)
	}

	const onStatusChanged = (e) => {
		setOrderStatus(e.value)
		setSyncOrderStatus(e.value === 'ALL' ? '' : e.value)
	}

	const onDivisionChanged = (e) => {
		setDivision(e.value)
	}

	const onStartDateChanged = (date) => {
		setStartDate(date)
		setSyncPeriod([date, endDate])
	}

	const onEndDateChanged = (date) => {
		setEndDate(date)
		setSyncPeriod([startDate, date])
	}

	const onNoteChanged = (e) => {
		setOrderNote(e.value)
	}

	const columns = getOrderReadyColumns()

	const filterNoteOrders = useMemo(() => {
		if (orderNote === OrderNotes.TRIAL_BATCH) return productionOrders.filter(ord => ord.trialBatch)
		return productionOrders
	}, [ productionOrders, orderNote ])

	return <div className="page-container">
		<div className='flex justify-between '>
			<h3>รายการ Order พร้อมผลิต (Release)</h3>
			<div>
				<form onSubmit={handleSubmit(onSubmitSearch)}>
					<div className='flex'>
						<span className='px-3 py-1.5 bg-gray-200 border border-[#CCCCCC] border-r-0'>#</span>
						<input type="text" placeholder="ค้นหาด้วย PD.Order No." {...register("pdOrderNo")}
							className='border border-[#CCCCCC] py-[0.375rem] px-3 w-full rounded-none'
							disabled={loading}
						></input>
					</div>
					<button
						type='submit'
						className='float-right mt-1 text-sm work-center-edit'>
						{loading ? 'กำลังค้นหา...' : 'ค้นหา PD Order ไม่เจอ ?'}
					</button>
				</form>
			</div>
		</div>
		<div className='grid grid-cols-3 gap-2 mb-2 lg:grid-cols-6'>
			<div>
        <small className='text-muted'>Division</small>
				<Select
					value={{ value: division, label: division }}
					onChange={onDivisionChanged}
					options={Object.keys(Division).map(stsKey => {
						return { value: DivisionValue[stsKey] ? DivisionValue[stsKey] : 'ALL', label: Division[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
        <small className='text-muted'>ชนิด Order</small>
				<Select
					value={{ value: orderType, label: orderType }}
					onChange={onTypeChanged}
					options={Object.keys(OrderType).map(typeKey => {
						return { value: OrderType[typeKey], label: OrderType[typeKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
        <small className='text-muted'>สถานะของ Order</small>
				<Select
					value={{ value: orderStatus, label: OrderStatusDisplayPdLeaderline[orderStatus] }}
					onChange={onStatusChanged}
					options={Object.keys(OrderStatusDisplayPdLeaderline).map(stsKey => {
						return { value: stsKey, label: OrderStatusDisplayPdLeaderline[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
        <small className='text-muted'>หมายเหตุ</small>
				<Select
					value={{ value: orderNote, label: OrderNoteDisplays[orderNote] }}
					onChange={onNoteChanged}
					options={Object.keys(OrderNoteDisplays).map(stsKey => {
						return { value: stsKey, label: OrderNoteDisplays[stsKey]}
					})}
					isSearchable={false}
				/>
			</div>
			<div>
        <small className='text-muted'>แสดงข้อมูลวันที่</small>
				<DatePicker
					className='input-form-date'
					selected={startDate}
					onChange={onStartDateChanged}
					selectsStart
					startDate={startDate}
					endDate={endDate}
					dateFormat="dd/MM/yyyy"
				/>
			</div>
			<div>
        <small className='text-muted'>ถึงวันที่</small>
				<DatePicker
					className='input-form-date'
					selected={endDate}
					onChange={onEndDateChanged}
					selectsEnd
					startDate={startDate}
					endDate={endDate}
					minDate={startDate}
					dateFormat="dd/MM/yyyy"
				/>
			</div>
		</div>
		<DataTable
			columns={columns}
			rows={filterNoteOrders}
		/>
	</div>
}

export default ProductionOrders